import {
  Box,
  CircularProgress,
  Container,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import {
  canLoadAppAtom,
  deploymentOptionsAtom,
  handleAppLoadAtom,
  handleAppLoadOnPremAtom,
  hasAccessToCurrentEnvAtom,
  isAppLoadedAtom,
  isStopDisabledAtom,
  playStatusAtom,
  selectedTextForPlayAtom,
  ttsCustomEditorDirtyAtom,
  userInputAtom,
  userPreferencesAtom,
  voicesDescriptionAtom,
} from "atoms";

import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { fetchQuotaUsageAtom } from "atoms/quota";
import { PlayStatus } from "shared/models";
import TTSAudioPlayer from "./TTSAudioPlayer";
import TTSSettings from "./TTSSettings";
import TTSCustomEditor, { TTSCustomEditorHandle } from "./TTSCustomEditor";
import { CommonBgColor, MenuBorderColor } from "shared/constants";
import { useTTS } from "shared/hooks/useTTS";
import NoDeploymentState from "./components/NoDeploymentState";
import { componentPageStyles } from "styles/component-page-styles";
import AccessDeniedNotification from "shared/components/AccessDeniedNotification";
import { TertiaryButton } from "@web-applications/allinga-web-components";
import LanguageVoiceSettings from "./TTSSettings/LanguageVoiceSettings";
import { runtimeConfig } from "@/shared/config/runtime-config-loader";
import TTSHelpAndLearning from "./TTSHelpAndLearning";
import SSMLMobileViewBanner from "shared/components/SSMLMobileViewBanner";
import DowntimeBanner from "./components/DowntimeBanner";

export default function TTSHomepage(): JSX.Element {
  const { t } = useTranslation();
  const userPreferences = useAtomValue(userPreferencesAtom);
  const handleAppLoad = useSetAtom(handleAppLoadAtom);
  const handleAppLoadOnPrem = useSetAtom(handleAppLoadOnPremAtom);
  const fetchQuota = useSetAtom(fetchQuotaUsageAtom);
  const canLoadApp = useAtomValue(canLoadAppAtom);
  const userInput = useAtomValue(userInputAtom);
  const [isStopDisabled, setStopDisabled] = useAtom(isStopDisabledAtom);
  const { streamTTSAudio } = useTTS();
  const setIsEditorDirty = useSetAtom(ttsCustomEditorDirtyAtom);
  const hasAccessToCurrentEnv = useAtomValue(hasAccessToCurrentEnvAtom);
  const [playStatus, setPlayStatus] = useAtom(playStatusAtom);
  const setSelectedTextForPlay = useSetAtom(selectedTextForPlayAtom);
  const deploymentOptions = useAtomValue(deploymentOptionsAtom);
  const [editorContent, setEditorContent] = useState<string>("");
  const [hasPlayError, setHasPlayError] = useState<boolean>(false);
  const [isAppLoaded, setIsAppLoaded] = useAtom(isAppLoadedAtom);
  const [showSettings, setShowSettings] = useState<boolean>(false);
  const voicesDescription = useAtomValue(voicesDescriptionAtom);
  const customEditorRef = useRef<TTSCustomEditorHandle>(null);

  // Analyser node from the streaming AudioContext, passed to waveform for visualization
  const [streamAnalyser, setStreamAnalyser] = useState<AnalyserNode | null>(null);

  const streamControlsRef = useRef<{
    stop: () => void;
    pause: () => void;
    resume: () => void;
    isPaused: () => boolean;
    analyser: AnalyserNode;
  } | null>(null);

  useEffect(() => {
    if (!isAppLoaded) {
      if (userPreferences._id !== "") {
        handleAppLoad();
        setIsAppLoaded(true);
      } else if (runtimeConfig.ttsOnPrem) {
        handleAppLoadOnPrem();
        setIsAppLoaded(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userPreferences, isAppLoaded]);

  // Handles editor content change
  const handleEditorChange = (content: string) => {
    setEditorContent(content);
    setIsEditorDirty(true);
    // Reset error state when user changes the content
    setHasPlayError(false);
  };

  const handlePlayClick = () => {
    // Reset error state when user tries to play again
    setHasPlayError(false);
    customEditorRef.current?.applyColoredSSML();

    // If streaming is active, toggle pause/resume
    if (streamControlsRef.current) {
      if (streamControlsRef.current.isPaused()) {
        streamControlsRef.current.resume();
      } else {
        streamControlsRef.current.pause();
      }
      return;
    }

    setPlayStatus(PlayStatus.InProgress);
  };

  // Send TTS request with streaming playback instead of waiting for full audio
  const sendTTSRequest = async (): Promise<void> => {
  if (isStopDisabled) {
    try {
      let playbackDidStart = false;

      const controls = await streamTTSAudio(false, {
        onPlaybackStarted: () => {
          playbackDidStart = true;
          setStopDisabled(false);
        },
        onPlaybackEnded: () => {
          // Refresh quota in case a server call was made
          void fetchQuota();
          setStopDisabled(true);
          setPlayStatus(PlayStatus.InActive);
          setSelectedTextForPlay("");
          streamControlsRef.current = null;
          setStreamAnalyser(null);
          // If playback never started, show error state
          if (!playbackDidStart) {
            setHasPlayError(true);
          }
        },
      });

      streamControlsRef.current = controls;
      setStreamAnalyser(controls.analyser);
    } catch (error) {
      // useTTS hook already handles error notifications including quota errors
      // Reset play status and signal error to reset UI state
      setPlayStatus(PlayStatus.InActive);
      setStopDisabled(true);
      setHasPlayError(true);
      streamControlsRef.current = null;
      setStreamAnalyser(null);
    }
  }
};

  useEffect(() => {
    // Make the TTS request only when the playStatus is Ready
    if (playStatus === PlayStatus.Ready) sendTTSRequest();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playStatus]);

  // Cancel the active stream when stopping
  const handleStopClick = () => {
    if (streamControlsRef.current) {
      streamControlsRef.current.stop();
      streamControlsRef.current = null;
    }
    // Refresh quota in case a partial server call
    void fetchQuota();
    setStopDisabled(true);
    setPlayStatus(PlayStatus.InActive);
    setSelectedTextForPlay("");
    setStreamAnalyser(null);
  };

  return canLoadApp ? (
    !hasAccessToCurrentEnv ? (
      <AccessDeniedNotification />
    ) : (
      <Stack
        alignItems="center"
        justifyContent="center"
        id="tts-homepage"
        bgcolor={CommonBgColor}
      >
        <DowntimeBanner />
        <Container sx={componentPageStyles.root}>
          <Stack
            sx={{
              padding: { xs: "32px 0 0 0", md: "40px 40px 24px 40px" },
            }}
          >
            <Grid container sx={{ position: "relative" }}>
              <Grid item xs={12} md={8} lg={8}>
                <Stack pb={1} gap={1}>
                  <Typography variant="h3" id="tts-homepage-header">
                    {t("tts.title")}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    {t("tts.description1")}{" "}
                  </Typography>
                </Stack>
              </Grid>
              <Grid
                item
                xs={12}
                md={4}
                lg={4}
                sx={{
                  position: { xs: "relative", md: "absolute" },
                  bottom: { md: 10 },
                  right: { md: 10 },
                  color: "primary.main",
                  pb: { sm: "16px", md: 0 },
                }}
              >
                <TertiaryButton
                  text={t("buttons.advancedSettings")}
                  id="adv-settings"
                  handleOnClick={() => setShowSettings(!showSettings)}
                  endIcon={
                    showSettings ? <ExpandLessIcon /> : <ExpandMoreIcon />
                  }
                />
              </Grid>
            </Grid>
            {/* Env and deployment */}
            <Stack
              sx={{
                display: { xs: "none", md: "block" },
              }}
            >
              {showSettings && <TTSSettings />}
            </Stack>
            <Stack
              sx={{
                padding: { xs: "0", md: "0 40px" },
                display: { xs: "block", md: "none" },
              }}
            >
              {showSettings && <TTSSettings />}
            </Stack>
            <SSMLMobileViewBanner />
          </Stack>
          <Stack px={{ xs: 0, md: 5 }} py={{ xs: 0, md: 3 }}>
            {deploymentOptions.length !== 0 && voicesDescription.length > 0 && (
              <LanguageVoiceSettings stopEditorAudio={handleStopClick} />
            )}
            <Stack
              sx={{
                borderBottom: {
                  xs: `1px solid ${MenuBorderColor}`,
                  sm: "none",
                },
              }}
            />
            {/* User input text field */}
            {deploymentOptions.length > 0 && (
              <TTSCustomEditor
                ref={customEditorRef}
                handleStopClick={handleStopClick}
                editorContent={editorContent}
                onEditorChange={handleEditorChange}
              />
            )}
            {/* Audio setup */}
            {deploymentOptions.length > 0 && (
              <TTSAudioPlayer
                onPlayClick={handlePlayClick}
                shouldDisableButtons={userInput.trim().length === 0}
                isStopDisabled={isStopDisabled}
                onStopClick={handleStopClick}
                showError={hasPlayError}
                analyser={streamAnalyser}
              />
            )}
            <Box sx={{ display: { xs: "none", md: "block" } }}>
              <TTSHelpAndLearning editorRef={customEditorRef} />
            </Box>
            {deploymentOptions.length === 0 && (
              <NoDeploymentState
                mainText={t("tts.noDeployment.mainText")}
                secondaryText={t("tts.noDeployment.secondaryText")}
              />
            )}
          </Stack>
        </Container>
      </Stack>
    )
  ) : (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh",
      }}
    >
      <CircularProgress />
    </Box>
  );
}